// SlipGenerator.js - Handles slip content generation
import {getClubLocation} from "../../../config/clubConfig";
import {getFolkLevelLocation} from "../../../config/folkLevelConfig";

export class SlipGenerator {
    constructor() {
        this.lateLevels = ["FOLK Prelims", "FOLK Graduate", "FOLK Enhanced", "FOLK Plus"];
        // SHREYANS printer auto cut commands
        this.FULL_CUT = new Uint8Array([0x1d, 0x56, 0x00]);
        this.FEED_LINES = new Uint8Array([0x1b, 0x64, 0x05]); // feed 5 lines
    }

    async printToShreyansDevice(slipContent) {
        try {
            // Connect to SHREYANS printer
            const device = await navigator.bluetooth.requestDevice({
                filters: [{namePrefix: "SHREYANS"}],
                optionalServices: [0x180f, 0x1812],
            });

            const server = await device.gatt.connect();
            const service = await server.getPrimaryService(0x1812);
            const characteristic = await service.getCharacteristic(0x2a4d);

            // Convert HTML content to plain text for thermal printer
            const textContent = this.convertHtmlToThermalText(slipContent);
            const encoder = new TextEncoder();
            const text = encoder.encode(textContent);

            // Write print text
            await characteristic.writeValue(text);

            // Feed some lines before cut
            await characteristic.writeValue(this.FEED_LINES);

            // Full cut command
            await characteristic.writeValue(this.FULL_CUT);

            console.log("Slip printed & cut successfully!");
            return true;
        } catch (err) {
            console.error("SHREYANS printer failed:", err);
            return false;
        }
    }

    convertHtmlToThermalText(htmlContent) {
        // Convert HTML slip content to thermal printer text format
        let text = htmlContent
        .replace(/<[^>]*>/g, "") // Remove HTML tags
        .replace(/&nbsp;/g, " ") // Replace HTML entities
        .replace(/&amp;/g, "&")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/\s+/g, " ") // Normalize whitespace
        .trim();

        // Format for thermal printer (80mm width)
        return (
            `\n${"*".repeat(32)}\n` +
            `    FOLK SUNDAY ATTENDANCE\n` +
            `${"*".repeat(32)}\n\n` +
            text +
            `\n\n${"=".repeat(32)}\n` +
            `   Hare Krishna !!\n` +
            `   Have a nice day\n` +
            `${"=".repeat(32)}\n\n`
        );
    }

    getClubLocation(clubName) {
        return getClubLocation(clubName);
    }

    escapeHtml(text) {
        if (!text) return "";
        const div = document.createElement("div");
        div.textContent = text;
        return div.innerHTML;
    }

    getCurrentISTTime() {
        const now = new Date();
        return new Date(now.toLocaleString("en-US", {timeZone: "Asia/Kolkata"}));
    }

    getDateStr() {
        return this.getCurrentISTTime().toLocaleString("en-IN", {
            month: "long",
            day: "2-digit",
        });
    }

    getTimeStr() {
        return this.getCurrentISTTime().toLocaleString("en-IN", {
            hour12: true,
            hour: "2-digit",
            minute: "2-digit",
        });
    }

    generateSlipContent(fetchedRecord, isDuplicate) {
        const currTime = this.getCurrentISTTime();
        const hours = currTime.getHours();
        const minutes = currTime.getMinutes();

        const isLate = hours > 16 || (hours === 16 && minutes > 30);
        const requiresFine = isLate && this.lateLevels.includes(fetchedRecord["FOLK Level (Display)"]);

        let slipHeaderStr = "";
        let signatureStr = `
            <div style="text-align: center; margin-top: 10mm; font-size: 6mm; font-weight: bold;">
                Hare Krishna !!<br/>Have a nice day
            </div>
            <div style="margin-top: 2mm; text-align: left; font-size: 10mm;">Sign</div>
            <div style="margin-top: 1mm; margin-bottom: 5mm; text-align: left; font-size: 7.5mm;">(FOLK Guide)</div>
             <br/><br/><br/><br/>`;

        if (isDuplicate) {
            if (isLate) {
                slipHeaderStr = `<div style="width: 100%; display: flex; justify-content: center; align-items: center; font-size: 10mm; background-color: black;
                color: white; font-weight: bold; padding: 4mm 0; margin: 0;">DUPLICATE & LATE ATTENDANCE</div>`;

                if (requiresFine) {
                    slipHeaderStr += `
                    <div style="width: 100%; text-align: center; font-size: 6mm; margin-top: 4mm;
                        font-weight: bold; border: 2px dashed black; padding: 3mm; line-height: 8mm;">
                        Please collect your paid prasadam coupon (Rs. 50) <br/>
                        from the FOLK Front Desk <br/> after 7:00 PM.
                    </div>`;
                }
            } else {
                slipHeaderStr = `<div style="width: 100%; display: flex; justify-content: center; align-items: center; font-size: 10mm; background-color: black;
                color: white; font-weight: bold; padding: 4mm 0; margin: 0;">DUPLICATE ATTENDANCE</div>`;
            }
            signatureStr = `
            <div style="text-align: center; margin-top: 10mm; font-size: 6mm; font-weight: bold;">
                Hare Krishna !!<br/>Have a nice day
            </div>
            <div style="margin-top: 2mm; text-align: left; font-size: 10mm;">Sign</div>
            <div style="margin-top: 1mm; margin-bottom: 5mm; text-align: left; font-size: 7.5mm;">(FOLK Guide)</div>
              <br/><br/><br/><br/>`;
        } else if (isLate) {
            slipHeaderStr = `<div style="width: 100%; display: flex; justify-content: center; align-items: center; font-size: 10mm; background-color: black;
            color: white; font-weight: bold; padding: 4mm 0; margin: 0;">LATE ATTENDANCE</div>`;

            if (requiresFine) {
                slipHeaderStr += `
                <div style="width: 100%; text-align: center; font-size: 6mm; margin-top: 4mm;
                    font-weight: bold; border: 2px dashed black; padding: 3mm; line-height: 8mm;">
                    Please collect your paid prasadam coupon (Rs. 50) <br/>
                    from the FOLK Front Desk <br/> after 7:00 PM.
                </div>`;
            }

            signatureStr = `
            <div style="text-align: center; margin-top: 10mm; font-size: 6mm; font-weight: bold;">
                Hare Krishna !!<br/>Have a nice day
            </div>
            <div style="margin-top: 2mm; text-align: left; font-size: 10mm;">Sign</div>
            <div style="margin-top: 1mm; margin-bottom: 5mm; text-align: left; font-size: 7.5mm;">(FOLK Guide)</div>
             <br/><br/><br/><br/>`;
        }

        return `
            ${slipHeaderStr}
            <div style="width: 100%; height: 1px; background-color: black; margin-top: 4mm;"></div>

            <div style="display: flex; flex-direction: column; align-items: flex-start; margin-top: 7mm; font-size: 7.5mm; font-weight: 600; width: 100%;">
                <div style="margin-bottom: 5mm; display: flex;">
                    <span style="font-weight: 800; width: 40mm; text-align: left;">Name:</span>
                    <span style="flex-grow: 1; font-weight: 600;">${
                        this.escapeHtml(fetchedRecord["HK Japa User Name"] || fetchedRecord["Name"]) ||
                        "<i>No Name Provided</i>"
                    }</span>
                </div>

                <!-- <div style="margin-bottom: 5mm; display: flex;">
                    <span style="font-weight: 800; width: 40mm; text-align: left;">Club:</span>
                    <span style="flex-grow: 1; font-weight: 600;">${this.escapeHtml(
                        fetchedRecord["FOLK Recent Club"]
                    )}</span>
                </div>
                <div style="margin-bottom: 5mm; display: flex;">
                    <span style="font-weight: 800; width: 40mm; text-align: left;">Location:</span>
                    <span style="flex-grow: 1; font-weight: 600;">${this.getClubLocation(
                        fetchedRecord["FOLK Recent Club"]
                    )}</span>
                </div> -->
                <div style="margin-bottom: 5mm; display: flex;">
                    <span style="font-weight: 800; width: 35mm; text-align: left;">F. Level:</span>
                    <span style="flex-grow: 1; font-weight: 600;">${this.escapeHtml(
                        fetchedRecord["FOLK Level (Display)"]
                    )}</span>
                </div>
                <div style="margin-bottom: 5mm; display: flex;">
                    <span style="font-weight: 800; width: 35mm; text-align: left;">Location:</span>
                    <span style="flex-grow: 1; font-weight: 600;">${getFolkLevelLocation(
                        fetchedRecord["FOLK Level (Display)"]
                    )}</span>
                </div>
                <div style="margin-bottom: 5mm; display: flex;">
                    <span style="font-weight: 800; width: 35mm; text-align: left;">A. Level:</span>
                    <span style="flex-grow: 1; font-weight: 600;">${this.escapeHtml(
                        fetchedRecord["Current Ashraya Level"]
                    )}</span>
                </div>
                <div style="margin-bottom: 5mm; display: flex;">
                    <span style="font-weight: 800; width: 35mm; text-align: left;">F. Guide:</span>
                    <span style="flex-grow: 1; font-weight: 600;">${this.escapeHtml(fetchedRecord["Guide Name"])}</span>
                </div>

                <div style="margin-bottom: 5mm; display: flex;">
                    <span style="font-weight: 800; width: 40mm; text-align: left;">Guide No.:</span>
                    <span style="flex-grow: 1; font-weight: 600;">${this.escapeHtml(
                        fetchedRecord["Guide Mobile No."]
                    )}</span>
                </div>
                <div style="margin-bottom: 5mm; display: flex;">
                    <span style="font-weight: 800; width: 40mm; text-align: left;">Date:</span>
                    <span style="flex-grow: 1; font-weight: 600;">${this.getDateStr()}</span>
                </div>
                <div style="margin-bottom: 5mm; display: flex;">
                    <span style="font-weight: 800; width: 40mm; text-align: left;">Time:</span>
                    <span style="flex-grow: 1; font-weight: 600;">${this.getTimeStr().toUpperCase()}</span>
                </div>
            </div>

            ${signatureStr}
            <div style="text-align: center; margin-top: 5mm;">
                <!-- SHREYANS printer auto cut will be handled by printToShreyansDevice method -->
            </div>
            `;
    }

    updateSlipDOM(content) {
        // Complete cleanup before new content
        this.hardResetSlipDOM();

        setTimeout(() => {
            const body = document.querySelector("body");
            const slip = document.getElementById("print-slip");
            const contentEl = document.getElementById("print-content");

            if (body && slip && contentEl) {
                body.classList.add("print");
                slip.classList.add("print");
                contentEl.innerHTML = content;
                contentEl.style.margin = "0";
                contentEl.style.padding = "0";
                contentEl.style.background = "#FFFFFF";
                void contentEl.offsetHeight;
            }
        }, 200);
    }

    hardResetSlipDOM() {
        try {
            console.log("HARD RESET SLIP DOM");

            // Remove all print classes
            const body = document.querySelector("body");
            if (body) {
                body.classList.remove("print");
                body.removeAttribute("style");
            }

            const slip = document.getElementById("print-slip");
            if (slip) {
                slip.classList.remove("print");
                slip.removeAttribute("style");
            }

            // Only clear the content area, preserve logo and header
            const content = document.getElementById("print-content");
            if (content) {
                while (content.firstChild) {
                    content.removeChild(content.firstChild);
                }
                content.innerHTML = "";
                content.removeAttribute("style");
                content.style.cssText = ""; // Clear all inline styles
            }

            // Remove ALL canvases and html2canvas artifacts
            document.querySelectorAll("canvas").forEach((c) => {
                try {
                    c.width = 0;
                    c.height = 0;
                    if (c.parentNode) c.parentNode.removeChild(c);
                } catch {}
            });

            // Remove html2canvas temporary containers
            document.querySelectorAll("[id^='html2canvas']").forEach((node) => {
                try {
                    if (node.parentNode) node.parentNode.removeChild(node);
                } catch {}
            });

            // Remove any stray print-related elements
            document.querySelectorAll(".html2canvas-container").forEach((el) => {
                try {
                    if (el.parentNode) el.parentNode.removeChild(el);
                } catch {}
            });

            // Force complete DOM reflow
            void document.body.offsetHeight;
            
            // Force garbage collection if available
            if (window.gc) window.gc();

            console.log("HARD RESET DONE");
        } catch (err) {
            console.error("Hard reset DOM failed", err);
        }
    }
}
